import { useMemo } from "react";
import { useCallback, useReducer } from "react";
import Error from "types/error";
import useSafeDispatch from "./useSafeDispatch";

enum AsyncStatus {
  idle = "idle",
  resolved = "resolved",
  rejected = "rejected",
  pending = "pending",
}

type State<Data> = {
  data?: Data | null;
  isLoading?: boolean;
  error?: Error | null;
  status?: AsyncStatus;
};

const defaultInitialState = {
  status: AsyncStatus.idle,
  data: null,
  error: null,
};

function useAsync<Data>(initialInputState?: State<Data>) {
  const initialState = useMemo(() => {
    return {
      ...defaultInitialState,
      ...initialInputState,
    };
  }, [initialInputState]);

  const [{ status, data, error }, setState] = useReducer(
    (state: State<Data>, action: State<Data>) => ({
      ...state,
      ...action,
    }),
    initialState
  );

  const safeSetState = useSafeDispatch(setState);

  const setData = useCallback(
    (newData) => safeSetState({ data: newData, status: AsyncStatus.resolved }),
    [safeSetState]
  );

  const setError = useCallback(
    (newError) =>
      safeSetState({ error: newError, status: AsyncStatus.rejected }),
    [safeSetState]
  );

  const reset = useCallback(
    () => safeSetState(initialState),
    [initialState, safeSetState]
  );

  const run = useCallback(
    (promise: Promise<any>) => {
      if (!promise || !promise.then) {
        throw new Error(
          `The argument passed to useAsync().run must be a promise.`
        );
      }

      safeSetState({ status: AsyncStatus.pending });
      return promise.then(
        (newData: Data) => {
          setData(newData);
          return newData;
        },
        (newError: Error) => {
          setError(newError);
          return Promise.reject(newError);
        }
      );
    },
    [safeSetState, setData, setError]
  );

  return {
    isIdle: status === AsyncStatus.idle,
    isLoading: status === AsyncStatus.pending,
    isError: status === AsyncStatus.rejected,
    isSuccess: status === AsyncStatus.resolved,
    setData,
    setError,
    error,
    status,
    data,
    run,
    reset,
  };
}

export { useAsync };
